/**
 * Hooks TanStack Query pour Chorus Pro
 * Pattern: Component → Hook → Service
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { chorusProAPI, chorusProSettingsAPI } from '@/utils/api';
import { notifications } from '@/lib/notifications';
import { notify } from '@/stores/notificationStore';
import { logger } from '@/utils/logger';

// ==================== Types ====================

export interface ChorusProInvoice {
  id: string;
  chorusProId: string;
  invoiceNumber: string;
  supplierName: string;
  amount: number;
  status: 'received' | 'pending' | 'approved' | 'rejected' | 'suspended';
  receivedDate: string;
  dueDate: string;
  format: string;
}

export interface ChorusProStats {
  totalInvoices: string;
  received: string;
  pending: string;
  approved: string;
  rejected: string;
  totalApprovedAmount: string | null;
}

export interface ChorusProConfig {
  id?: string;
  companyId: string;
  isEnabled: boolean;
  clientId?: string;
  siret?: string;
  environment: string;
}

// ==================== Query Keys ====================

export const chorusProKeys = {
  all: ['chorusPro'] as const,
  invoices: (filter?: string) => [...chorusProKeys.all, 'invoices', filter] as const,
  stats: () => [...chorusProKeys.all, 'stats'] as const,
  config: () => [...chorusProKeys.all, 'config'] as const,
};

// ==================== Queries ====================

/**
 * Hook pour récupérer les factures Chorus Pro
 */
export function useChorusProInvoices(filter?: string) {
  return useQuery<ChorusProInvoice[]>({
    queryKey: chorusProKeys.invoices(filter),
    queryFn: async () => {
      const data = await chorusProAPI.getInvoices(filter !== 'all' ? filter : undefined);
      return data.success ? data.data : [];
    },
  });
}

/**
 * Hook pour récupérer les statistiques Chorus Pro
 */
export function useChorusProStats() {
  return useQuery<ChorusProStats>({
    queryKey: chorusProKeys.stats(),
    queryFn: async () => {
      const data = await chorusProAPI.getStatistics();
      return data.success ? data.data : {
        totalInvoices: '0', received: '0', pending: '0',
        approved: '0', rejected: '0', totalApprovedAmount: '0',
      };
    },
  });
}

/**
 * Hook pour récupérer la configuration Chorus Pro
 */
export function useChorusProConfig() {
  return useQuery<ChorusProConfig | null>({
    queryKey: chorusProKeys.config(),
    queryFn: async () => {
      const data = await chorusProSettingsAPI.getConfig();
      return data.success && data.data ? data.data : null;
    },
  });
}

// ==================== Mutations ====================

/**
 * Hook pour approuver une facture Chorus Pro
 */
export function useApproveChorusProInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, comment }: { invoiceId: string; comment: string }) =>
      chorusProAPI.approveInvoice(invoiceId, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chorusProKeys.all });
      notifications.success('Facture approuvée');
    },
    onError: (error: Error) => {
      logger.error('Erreur approbation facture:', error);
      notifications.error('Erreur lors de l\'approbation');
    },
  });
}

/**
 * Hook pour rejeter une facture Chorus Pro
 */
export function useRejectChorusProInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, reason, comment }: { invoiceId: string; reason: string; comment: string }) =>
      chorusProAPI.rejectInvoice(invoiceId, reason, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chorusProKeys.all });
      notifications.success('Facture rejetée');
    },
    onError: (error: Error) => {
      logger.error('Erreur rejet facture:', error);
      notifications.error('Erreur lors du rejet');
    },
  });
}

/**
 * Hook pour sauvegarder la configuration Chorus Pro
 */
export function useSaveChorusProConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: {
      isEnabled: boolean;
      clientId: string;
      clientSecret: string;
      siret: string;
      environment: string;
    }) => chorusProSettingsAPI.upsertConfig(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: chorusProKeys.config() });
      notify.success(data.message || 'Configuration enregistrée', 'Succès');
    },
    onError: (error: Error) => {
      logger.error('Erreur sauvegarde config:', error);
      notify.error(error.message || 'Erreur lors de la sauvegarde', 'Erreur');
    },
  });
}

/**
 * Hook pour tester la connexion Chorus Pro
 */
export function useTestChorusProConnection() {
  return useMutation({
    mutationFn: ({ clientId, clientSecret, environment }: {
      clientId: string;
      clientSecret: string;
      environment: string;
    }) => chorusProSettingsAPI.testConnection(clientId, clientSecret, environment),
    onSuccess: (data) => {
      if (data.success) {
        notify.success('Connexion réussie à Chorus Pro', 'Succès');
      } else {
        notify.error(data.message || 'Échec de la connexion', 'Erreur');
      }
    },
    onError: (error: Error) => {
      logger.error('Erreur test connexion:', error);
      notify.error(error.message || 'Erreur lors du test de connexion', 'Erreur');
    },
  });
}

/**
 * Hook pour désactiver Chorus Pro
 */
export function useDisableChorusPro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => chorusProSettingsAPI.disableConfig(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chorusProKeys.config() });
      notify.success('Chorus Pro désactivé', 'Succès');
    },
    onError: (error: Error) => {
      logger.error('Erreur désactivation:', error);
      notify.error(error.message || 'Erreur lors de la désactivation', 'Erreur');
    },
  });
}
